import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { FormField } from '../components/FormField';
import { TextInput } from '../components/TextInput';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { HelpTip } from '../components/HelpTip';
import { toDisplayError, toFieldErrors } from '../api/errorMessages';
import styles from './ProjectForm.module.css';

export interface ProjectValues {
  name: string;
  webPropertyUrl: string;
  authorizedHost: string;
}

interface FieldErrors {
  name?: string;
  webPropertyUrl?: string;
  authorizedHost?: string;
}

interface FormSummary {
  message: string;
  requestId?: string;
}

export interface ProjectFormProps {
  initialValues: ProjectValues;
  submitLabel: string;
  busyLabel: string;
  onSubmit: (values: ProjectValues) => Promise<void>;
  /** Optional content rendered under the heading (e.g. a status badge on edit). */
  headerExtra?: ReactNode;
}

const HOST_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i;

/** Client-side *format hints* only — the server is authoritative on whether a host
 * is allowed (private/loopback/metadata rejections arrive as a 422 field error). */
function hostFormatHint(host: string): string | undefined {
  const value = host.trim();
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value) || value.includes('/') || /\s/.test(value)) {
    return 'Enter a bare hostname, e.g. shop.example.com (no https:// or path).';
  }
  if (!HOST_RE.test(value)) {
    return 'This does not look like a valid hostname.';
  }
  return undefined;
}

function urlFormatHint(url: string): string | undefined {
  const value = url.trim();
  if (!value) return undefined;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'Enter an http(s) URL, e.g. https://shop.example.com.';
    }
  } catch {
    return 'Enter a valid URL, e.g. https://shop.example.com.';
  }
  return undefined;
}

function validate(values: ProjectValues): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.name.trim()) errors.name = 'Enter a project name.';
  if (!values.webPropertyUrl.trim()) {
    errors.webPropertyUrl = 'Enter the storefront URL.';
  } else {
    const hint = urlFormatHint(values.webPropertyUrl);
    if (hint) errors.webPropertyUrl = hint;
  }
  if (!values.authorizedHost.trim()) {
    errors.authorizedHost = "Enter your store's domain.";
  } else {
    const hint = hostFormatHint(values.authorizedHost);
    if (hint) errors.authorizedHost = hint;
  }
  return errors;
}

export function ProjectForm({
  initialValues,
  submitLabel,
  busyLabel,
  onSubmit,
  headerExtra,
}: ProjectFormProps) {
  const [values, setValues] = useState<ProjectValues>(initialValues);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [summary, setSummary] = useState<FormSummary | null>(null);
  const [busy, setBusy] = useState(false);

  const summaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (summary) summaryRef.current?.focus();
  }, [summary]);

  const set = (key: keyof ProjectValues) => (value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const onBlurHost = () => {
    const hint = hostFormatHint(values.authorizedHost);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (hint) next.authorizedHost = hint;
      else delete next.authorizedHost;
      return next;
    });
  };
  const onBlurUrl = () => {
    const hint = urlFormatHint(values.webPropertyUrl);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (hint) next.webPropertyUrl = hint;
      else delete next.webPropertyUrl;
      return next;
    });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;

    const errors = validate(values);
    if (errors.name || errors.webPropertyUrl || errors.authorizedHost) {
      setFieldErrors(errors);
      setSummary({ message: 'Please fix the errors below and try again.' });
      return;
    }

    setFieldErrors({});
    setSummary(null);
    setBusy(true);
    try {
      await onSubmit({
        name: values.name.trim(),
        webPropertyUrl: values.webPropertyUrl.trim(),
        authorizedHost: values.authorizedHost.trim(),
      });
    } catch (err) {
      // A 422 carries per-field discriminators (rejected host/url). Map those to
      // the specific fields; anything else is a form-level message.
      const fields = toFieldErrors(err);
      const mapped: FieldErrors = {};
      if (fields.name) mapped.name = fields.name;
      if (fields.webPropertyUrl) mapped.webPropertyUrl = fields.webPropertyUrl;
      if (fields.authorizedHost) mapped.authorizedHost = fields.authorizedHost;

      if (mapped.name || mapped.webPropertyUrl || mapped.authorizedHost) {
        setFieldErrors(mapped);
        setSummary({ message: 'Please fix the errors below and try again.' });
      } else {
        const display = toDisplayError(err);
        setSummary({
          message: display.message,
          ...(display.requestId ? { requestId: display.requestId } : {}),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      {headerExtra}

      <div className={styles.summary}>
        <InlineAlert
          ref={summaryRef}
          severity="error"
          {...(summary?.requestId ? { requestId: summary.requestId } : {})}
        >
          {summary?.message}
        </InlineAlert>
      </div>

      <FormField label="Project name" id="project-name" {...(fieldErrors.name ? { error: fieldErrors.name } : {})}>
        {({ controlProps }) => (
          <TextInput
            {...controlProps}
            type="text"
            name="project-name"
            value={values.name}
            onChange={(e) => set('name')(e.target.value)}
          />
        )}
      </FormField>

      <FormField
        label="Storefront URL"
        id="web-property-url"
        helpText="The public storefront to test, e.g. https://shop.example.com."
        {...(fieldErrors.webPropertyUrl ? { error: fieldErrors.webPropertyUrl } : {})}
      >
        {({ controlProps }) => (
          <TextInput
            {...controlProps}
            type="text"
            name="web-property-url"
            value={values.webPropertyUrl}
            onChange={(e) => set('webPropertyUrl')(e.target.value)}
            onBlur={onBlurUrl}
          />
        )}
      </FormField>

      <FormField
        label="Store domain"
        id="authorized-host"
        labelHelp={<HelpTip term="storeDomain" />}
        helpText="Your store's domain, e.g. shop.example.com — the public site we'll watch. No https:// or path."
        {...(fieldErrors.authorizedHost ? { error: fieldErrors.authorizedHost } : {})}
      >
        {({ controlProps }) => (
          <TextInput
            {...controlProps}
            type="text"
            name="authorized-host"
            value={values.authorizedHost}
            onChange={(e) => set('authorizedHost')(e.target.value)}
            onBlur={onBlurHost}
          />
        )}
      </FormField>

      <div className={styles.actions}>
        <Button type="submit" busy={busy} fullWidth>
          {busy ? busyLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
}
